import { ReactNode, useEffect, useRef } from 'react'
import { type BaseBudget } from 'typings'
import { useAnalytics } from 'use-analytics'
import { useFormStateContext } from '../../../../providers'
import { trackBudgetView } from '../trackBudget'
import { BudgetDisabledView } from './Variants/Disabled'
import { BudgetEmptyView } from './Variants/Empty'
import { BudgetView } from './Variants/Filled'
import { BudgetUnauthorizedView } from './Variants/UnauthorizedView'
import { BudgetViewState } from './useBudgetViewState'

type BudgetViewRendererProps = {
  viewState: BudgetViewState
  budget: BaseBudget | undefined
  isAllowedDiscountType: boolean
  onAdd: () => void
  onRemove: () => void
}

const VIEW_COMPONENTS: Record<BudgetViewState, (props: any) => ReactNode> = {
  [BudgetViewState.EMPTY]: ({ onAdd, isAllowedDiscountType }) => (
    <BudgetEmptyView
      onAdd={onAdd}
      isAllowedDiscountType={isAllowedDiscountType}
    />
  ),
  [BudgetViewState.ACTIVE]: ({ budget, onAdd, onRemove }) => (
    <BudgetView config={budget} onAdd={onAdd} onRemove={onRemove} />
  ),
  [BudgetViewState.DISABLED_DISCOUNT_TYPE]: ({ budget, onRemove }) => (
    <BudgetDisabledView
      config={budget}
      reason="discount-type"
      onRemove={onRemove}
    />
  ),
  [BudgetViewState.DISABLED_BUDGET]: ({ budget, onRemove }) => (
    <BudgetDisabledView
      config={budget}
      reason="budget-disabled"
      onRemove={onRemove}
    />
  ),
  [BudgetViewState.UNAUTHORIZED]: BudgetUnauthorizedView,
  [BudgetViewState.LOADING]: () => null,
  [BudgetViewState.INVALID]: () => null,
}

export function BudgetViewRenderer(props: BudgetViewRendererProps): ReactNode {
  const { viewState, budget, isAllowedDiscountType, onAdd, onRemove } = props

  const ViewComponent = VIEW_COMPONENTS[viewState]

  const { track } = useAnalytics()

  const { form, discountType } = useFormStateContext()

  const lastViewTracked = useRef<BudgetViewState | undefined>()

  useEffect(() => {
    if (lastViewTracked.current !== viewState) {
      const formValues = form.getValues()
      trackBudgetView({
        promotionInfo: {
          promotionId: formValues.id,
          promotionType: formValues.type,
          benefitType: formValues[formValues.type]?.benefit?.type,
          discountType,
        },
        viewState,
        track,
      })
      lastViewTracked.current = viewState
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, track])

  if (!ViewComponent) {
    return null
  }

  return ViewComponent({
    budget,
    isAllowedDiscountType,
    onAdd,
    onRemove,
  })
}
